<script setup lang="ts">
// src/SideWinder.Web/src/components/strategy/SignalLogPanel.vue
//
// Task 8: recent strategy_signal event log (GET
// /api/admin/strategy/signals?since=...). Polled on a 30s interval rather
// than a live pubsub subscription — the coordinator's ruling on this task
// found that `events:admin` (mentioned in the original brief) does not
// exist; the only Redis events surface is `events:log`, a capped LIST, not
// a pub/sub channel. The carousel signal fires once per day at 07:35 UK,
// so a 30s poll delay is an acceptable cost — see task-8-brief.md ruling.
import { ref, onMounted, onUnmounted } from 'vue'
import { api, type StrategyEventRow } from '../../api/client'

const POLL_INTERVAL_MS = 30_000

const signals = ref<StrategyEventRow[]>([])
const loading = ref(true)
const loadError = ref<string | null>(null)
let timer: ReturnType<typeof setInterval> | null = null

async function refresh() {
  try {
    signals.value = await api.adminStrategySignals()
    loadError.value = null
  } catch (e: any) {
    loadError.value = e?.message ?? 'Failed to load signal log'
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  refresh()
  timer = setInterval(refresh, POLL_INTERVAL_MS)
})
onUnmounted(() => {
  if (timer) clearInterval(timer)
})

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' })
  } catch {
    return iso
  }
}
</script>

<template>
  <div class="card">
    <div class="card-header">
      Signal Log
      <span class="header-count">{{ signals.length }}</span>
      <span class="poll-hint">polls every 30s</span>
    </div>
    <div class="card-body">
      <div v-if="loading" class="loading-text">Loading signal log…</div>
      <div v-else-if="loadError" class="msg-error">{{ loadError }}</div>
      <div v-else-if="signals.length === 0" class="empty-text">No strategy_signal events in the current window</div>
      <div v-else class="table-wrap">
        <table class="data-table">
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>User</th>
              <th>Fund</th>
              <th>Message</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in signals" :key="row.event_id">
              <td class="date-cell">{{ fmtDateTime(row.timestamp) }}</td>
              <td class="mono-cell">{{ row.user_id ?? '—' }}</td>
              <td class="mono-cell">{{ row.fund_id ?? '—' }}</td>
              <td>{{ row.message }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<style scoped>
.card {
  background: #161b22;
  border: 1px solid #21262d;
  border-radius: 8px;
  overflow: hidden;
}
.card-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  background: #1c2128;
  border-bottom: 1px solid #21262d;
  font-size: 0.85rem;
  font-weight: 600;
  color: #e6edf3;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.header-count {
  font-size: 0.75rem;
  font-weight: 400;
  color: #6e7681;
  background: #21262d;
  padding: 1px 6px;
  border-radius: 10px;
}
.poll-hint {
  margin-left: auto;
  font-size: 0.7rem;
  font-weight: 400;
  text-transform: none;
  letter-spacing: normal;
  color: #6e7681;
}
.card-body { padding: 0.75rem 1rem; }

.table-wrap { overflow-x: auto; }
.data-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
.data-table th {
  text-align: left;
  padding: 6px 10px;
  font-size: 0.7rem;
  color: #6e7681;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #21262d;
  white-space: nowrap;
}
.data-table td { padding: 7px 10px; border-bottom: 1px solid #161b22; vertical-align: middle; color: #c9d1d9; }
.data-table tbody tr:hover { background: #1c2128; }
.data-table tbody tr:last-child td { border-bottom: none; }

.mono-cell { font-family: 'JetBrains Mono', monospace; font-size: 0.8rem; }
.date-cell { white-space: nowrap; font-size: 0.8rem; color: #8b949e; }

.loading-text, .empty-text { padding: 0.75rem 0; color: #6e7681; font-size: 0.9rem; font-style: italic; }
.msg-error {
  margin-top: 0.5rem;
  padding: 0.4rem 0.75rem;
  background: rgba(248,81,73,0.1);
  border: 1px solid #f85149;
  border-radius: 4px;
  font-size: 0.85rem;
  color: #f85149;
}

@media (max-width: 600px) {
  .data-table th, .data-table td { padding: 5px 6px; }
}
</style>
